import { gql, useApolloClient } from "@apollo/client";
import React from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useOrgConfig } from "src/hooks/useOrgConfig";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";

type Form = {
  first_name: string;
  last_name: string;
  middle_name: string;
  birth_date: string;
};
export type DuplicateStudent =
  | { status: "NotAsked" }
  | { status: "NotFound" }
  | { status: "FoundOnOtherAccounts" }
  | { status: "FoundOnParent"; studentId: uuid };
export function useFindDuplicateStudent() {
  const flags = useFlags([
    "prevent-duplicate-student-name-dob-across-accounts",
  ]);

  const [duplicateStudent, setDuplicateStudent] =
    React.useState<DuplicateStudent>({
      status: "NotAsked",
    });

  const apolloClient = useApolloClient();
  const findDuplicateStudent = React.useCallback(
    async (
      form: Form,
      parentId: uuid,
      organizationId: uuid,
      personId?: uuid
    ): Promise<"NotFound" | "FoundOnOtherAccounts" | "FoundOnParent"> => {
      setDuplicateStudent({ status: "NotAsked" });

      if (
        !flags["prevent-duplicate-student-name-dob-across-accounts"].enabled
      ) {
        return "NotFound";
      }

      const result = await apolloClient.query<
        GQL.FindDuplicateStudent,
        GQL.FindDuplicateStudentVariables
      >({
        query: gql`
          query FindDuplicateStudent(
            $parentId: uuid!
            $organizationId: uuid!
            $firstName: String!
            $lastName: String!
            $middleName: String!
            $dateOfBirth: date!
            $personId: uuid
          ) {
            find_duplicate_student(
              args: {
                parent_id: $parentId
                organization_id: $organizationId
                first_name: $firstName
                middle_name: $middleName
                last_name: $lastName
                birth_date: $dateOfBirth
                person_id: $personId
              }
            ) {
              is_found
              student_id
            }
          }
        `,
        variables: {
          parentId: parentId,
          organizationId: organizationId,
          firstName: form.first_name.trim(),
          lastName: form.last_name.trim(),
          middleName: "", // Exclude middle name from duplicate detection
          dateOfBirth: form.birth_date,
          personId: personId ?? null,
        },
        fetchPolicy: "network-only",
      });

      const duplicate = result.data.find_duplicate_student[0];
      if (duplicate === undefined) {
        setDuplicateStudent({ status: "NotFound" });
        return "NotFound";
      }

      if (duplicate.is_found && duplicate.student_id !== null) {
        setDuplicateStudent({
          status: "FoundOnParent",
          studentId: duplicate.student_id,
        });
        return "FoundOnParent";
      }

      if (
        flags["prevent-duplicate-student-name-dob-across-accounts"].enabled &&
        duplicate.is_found &&
        duplicate.student_id === null
      ) {
        setDuplicateStudent({ status: "FoundOnOtherAccounts" });
        return "FoundOnOtherAccounts";
      }

      setDuplicateStudent({ status: "NotFound" });
      return "NotFound";
    },
    [apolloClient, flags]
  );

  const isInvalid = React.useMemo(() => {
    return (
      duplicateStudent.status === "FoundOnParent" ||
      duplicateStudent.status === "FoundOnOtherAccounts"
    );
  }, [duplicateStudent.status]);

  const configRD = useOrgConfig<OrgConfig.AccountLookupConfig>("AccountLookup");
  const orgPath = useOrganizationPath();
  const helpLink = React.useMemo(() => {
    if (
      configRD.hasData() &&
      configRD.data !== null &&
      configRD.data.disabled === false
    ) {
      return configRD.data.supportUrl;
    }

    if (orgPath) {
      return Url.Parent.help({ path: orgPath });
    }

    return null;
  }, [configRD, orgPath]);

  const reset = React.useCallback(() => {
    setDuplicateStudent({ status: "NotAsked" });
  }, []);

  return { findDuplicateStudent, duplicateStudent, isInvalid, helpLink, reset };
}
